import {
  daySummary,
  DEFAULT_PERSO_MARGIN_MIN,
  departureLeadMin,
  formatDegrees,
  holidayOn,
  hourAt,
  persoConflicts,
  rideEmoji,
  suitUp,
  weatherEmoji,
  type AppointmentWithDisplay,
  type HomeSettings,
  type LocalDateTime,
  type MeteoHour,
  type MeteoWindow,
  type PersoEvent,
  type TravelLeg,
} from '@ergo/shared';
import { Fragment, createContext, useContext, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { STATUS_LABELS, VISIO_GLYPH, patientHue, titleOf } from '../appointments';
import { MapCanvas } from '../components/MapCanvas';
import { OutcomeButtons } from '../components/OutcomeButtons';
import { useResource } from '../hooks';
import { holidayUntilLabel, useSchoolHolidays } from '../holidays';
import { type MapPoint, useMapsKey } from '../maps';
import { useMeteoDay } from '../meteo';
import { conflictLabel, persoOfDay, usePersoEvents } from '../perso';
import {
  addDays,
  addMinutes,
  endsAt,
  formatDuration,
  formatLongDate,
  isValidDate,
  now,
  timeOf,
  today,
} from '../time';
import { hopOf, legHome, legInto } from '../travel';
import { useUnlogged } from '../unlogged';

/* Les heures de la journée, pour la petite météo sous chaque heure de la frise. */
const MeteoHoursContext = createContext<MeteoHour[]>([]);

/** Sous une heure de la frise : le ciel et la température à ce moment-là. */
function TimeWeather({ at }: { at: string | null }) {
  const hours = useContext(MeteoHoursContext);
  const hour = at === null ? null : hourAt(hours, at);
  if (hour === null) return null;
  const emoji = weatherEmoji(hour.code);
  return (
    <small className="timeline__meteo">
      {emoji ?? ''} {formatDegrees(hour.tempC)}
    </small>
  );
}

export function Aujourdhui() {
  const [search, setSearch] = useSearchParams();
  const requested = search.get('date');
  const date = requested && isValidDate(requested) ? requested : today();

  const appointments = useResource(() => api.appointments.list({ from: date, to: date }), [date]);
  const travel = useResource(() => api.maps.travelTimes(date), [date]);
  const settings = useResource(() => api.settings.get(), []);
  const perso = usePersoEvents(date, date);
  const holidays = useSchoolHolidays(date, date);
  const meteo = useMeteoDay(date);
  const maps = useMapsKey();
  const unlogged = useUnlogged();
  const returnTo = `/aujourdhui?date=${date}`;
  const currentTime = useClock();

  const items = appointments.data ?? [];
  const holiday = holidayOn(date, holidays);
  const { timed: persoTimed, allDay: persoAllDay } = persoOfDay(perso, date);
  const persoMarginMin = settings.data?.persoMarginMin ?? DEFAULT_PERSO_MARGIN_MIN;
  const home = settings.data?.home ?? null;
  const homeCardShown = Boolean(home?.formattedAddress ?? home?.address);
  const stops = items.filter(isStop);
  const firstStop = stops[0] ?? null;
  // The last séance she actually rides to: the one the ride home starts from.
  const lastStop = stops.at(-1) ?? null;
  // The « maintenant » marker slots in before the first séance still to come.
  const upcoming = items.findIndex((item) => item.startsAt > currentTime);
  const nowAt = date === today() && items.length > 0 ? (upcoming === -1 ? items.length : upcoming) : -1;

  // Keep the rail chronological around the two « Maison » cards: before the day
  // starts the marker sits above the departure card, after it ends it descends
  // past the return row and card as the clock passes each of their times.
  const nowTime = timeOf(currentTime);
  const departureTime = firstStop
    ? (hopOf(legInto(travel.data?.legs, firstStop.id), firstStop.startsAt).departure ??
      timeOf(firstStop.startsAt))
    : null;
  const nowBeforeDeparture =
    nowAt === 0 && homeCardShown && departureTime !== null && nowTime < departureTime;
  const returnLeg = legHome(travel.data?.legs);
  const lastEndTime = lastStop ? timeOf(endsAt(lastStop.startsAt, lastStop.plannedDurationMin)) : null;
  const arrivalTime = lastStop ? arrivalHome(lastStop, returnLeg) : null;
  const endMarkerPos = !(nowAt === items.length && items.length > 0)
    ? null
    : !homeCardShown || lastEndTime === null || nowTime <= lastEndTime
      ? 'beforeRow'
      : arrivalTime !== null && nowTime <= arrivalTime
        ? 'beforeCard'
        : 'after';

  /**
   * Les créneaux qu'elle passe sur le vélo : chaque trajet vers une séance, de
   * l'heure du départ à celle du rendez-vous, plus le retour à la maison. Ce sont
   * exactement les lignes 🚲 de la timeline — la météo ne parle que de ces heures-là.
   */
  const rideWindows = useMemo<MeteoWindow[]>(() => {
    const windows: MeteoWindow[] = [];
    for (const stop of items.filter(isStop)) {
      const leg = legInto(travel.data?.legs, stop.id);
      // Zéro minute : les deux bouts sont la même porte, il n'y a pas de trajet.
      if (leg === null || leg.bikeMinutes === 0) continue;
      const { departure } = hopOf(leg, stop.startsAt);
      if (departure !== null) windows.push({ from: departure, to: timeOf(stop.startsAt) });
    }
    if (returnLeg && lastEndTime !== null && arrivalTime !== null) {
      windows.push({ from: lastEndTime, to: arrivalTime });
    }
    return windows;
  }, [items, travel.data, returnLeg, lastEndTime, arrivalTime]);

  const summary = daySummary(meteo.hours);
  const suitUpLines = useMemo(() => suitUp(meteo.hours, rideWindows), [meteo.hours, rideWindows]);

  /**
   * Les rendez-vous perso, glissés entre les séances à leur heure : une case de plus
   * par intervalle, la dernière recueillant ce qui vient après la dernière séance.
   * Aucun trajet ne les relie à quoi que ce soit — l'app ne sait pas où elle va, et
   * les temps de vélo entre séances restent ceux d'avant.
   */
  const persoSlots = useMemo(() => {
    const slots: PersoEvent[][] = Array.from({ length: items.length + 1 }, () => []);
    for (const event of persoTimed) {
      const index = items.findIndex((item) => item.startsAt > event.startsAt);
      slots[index === -1 ? items.length : index]?.push(event);
    }
    return slots;
  }, [items, persoTimed]);

  // Après la dernière séance, la journée continue autour des deux repères « Maison » :
  // un rendez-vous perso se place selon l'heure — avant le départ du retour, pendant
  // le trajet, ou une fois rentrée — et non en bloc avant « Rentrer à la maison ».
  // Même logique en tête de journée : un rendez-vous perso avant l'heure du départ
  // se place au-dessus de la carte « Maison · départ », pas en dessous.
  const leading = persoSlots[0] ?? [];
  const departureShown = firstStop !== null && departureTime !== null;
  const persoBeforeDeparture = leading.filter(
    (event) => departureShown && timeOf(event.startsAt) < departureTime,
  );
  const persoAfterDeparture = leading.filter(
    (event) => !departureShown || timeOf(event.startsAt) >= departureTime,
  );
  const trailing = persoSlots[items.length] ?? [];
  const persoBeforeReturn = trailing.filter(
    (event) => lastEndTime === null || timeOf(event.startsAt) < lastEndTime,
  );
  const persoDuringReturn = trailing.filter(
    (event) =>
      lastEndTime !== null &&
      timeOf(event.startsAt) >= lastEndTime &&
      (arrivalTime === null || timeOf(event.startsAt) < arrivalTime),
  );
  const persoAfterReturn = trailing.filter(
    (event) => arrivalTime !== null && timeOf(event.startsAt) >= arrivalTime,
  );

  function onLogged() {
    appointments.reload();
    travel.reload();
    unlogged.refresh();
  }

  function goTo(value: string) {
    setSearch(value === today() ? {} : { date: value }, { replace: true });
  }

  return (
    <section className="page">
      <header className="day__header">
        <button
          type="button"
          className="button button--icon"
          aria-label="Jour précédent"
          onClick={() => goTo(addDays(date, -1))}
        >
          ‹
        </button>
        <div className="day__title">
          <h1 className="page__title">
            {formatLongDate(date)}
            {/* Le temps qu'il fera, en deux signes, à côté de la date. Rien du tout
                quand la météo est éteinte, sans maison, ou hors de la prévision. */}
            {summary && (
              <span className="day__meteo">
                {summary.emoji} {formatDegrees(summary.tempC)}
              </span>
            )}
          </h1>
          <input
            className="day__picker"
            type="date"
            value={date}
            aria-label="Choisir un jour"
            onChange={(event) => event.target.value && goTo(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="button button--icon"
          aria-label="Jour suivant"
          onClick={() => goTo(addDays(date, 1))}
        >
          ›
        </button>
      </header>

      {/* Les écoles sont fermées : la journée n'en devient pas vide, mais elle
          s'explique. Rien n'est déplacé — c'est un mot, pas une décision. */}
      {holiday && (
        <p className="vacances-banner">
          {holiday.name} · {holidayUntilLabel(holiday)}
        </p>
      )}

      {/* « prends la tenue » : ce que la journée demande d'emporter, trois lignes au
          plus. Même ton que les vacances et les rendez-vous perso — jamais de rouge,
          c'est un conseil, pas une alerte (docs/redesign-plus-douce.md). */}
      {suitUpLines.map((line) => (
        <p className="note-sun note-sun--jour" key={line} role="status">
          {line}
        </p>
      ))}

      {date !== today() && (
        <button type="button" className="button button--small" onClick={() => goTo(today())}>
          Revenir à aujourd’hui
        </button>
      )}

      {appointments.error && <p className="error" role="alert">{appointments.error}</p>}
      {appointments.loading && !appointments.data && <p className="muted">Chargement…</p>}

      {/* Une journée entière n'a pas d'heure : elle coiffe le jour, avant la timeline. */}
      {persoAllDay.map((event) => (
        <p className="perso-banner" key={event.uid}>
          {event.title}
        </p>
      ))}

      {appointments.data && appointments.data.length === 0 && (
        <p className="empty">Aucune séance ce jour-là.</p>
      )}

      {(items.length > 0 || persoTimed.length > 0) && (
        <MeteoHoursContext.Provider value={meteo.hours}>
        <ol className="timeline">
          {persoBeforeDeparture.map((event) => (
            <PersoCard event={event} key={event.uid} />
          ))}
          {nowBeforeDeparture && <NowMarker at={currentTime} />}
          {firstStop && departureTime !== null && (
            <HomeCard home={home} time={departureTime} label="départ" />
          )}

          {items.map((item, index) => {
            const past = endsAt(item.startsAt, item.plannedDurationMin) <= currentTime;
            const off = item.status === 'cancelled' || item.status === 'no_show';
            const classes = ['timeline__entry'];
            if (past) classes.push('is-past');
            if (off) classes.push('is-off');
            if (item.important) classes.push('is-important');
            const leg = legInto(travel.data?.legs, item.id);
            // La fenêtre occupée commence au départ à vélo, pas au début de la séance.
            const leavesAt = hopOf(leg, item.startsAt).estimate;
            const clashes = off
              ? []
              : persoConflicts(
                  {
                    startsAt: item.startsAt,
                    durationMin: item.plannedDurationMin,
                    leavesAt:
                      leavesAt === null ? null : addMinutes(item.startsAt, -leavesAt),
                  },
                  persoTimed,
                  persoMarginMin,
                ).filter((clash) => clash.overlaps);

            return (
              <Fragment key={item.id}>
                {(index === 0 ? persoAfterDeparture : (persoSlots[index] ?? [])).map((event) => (
                  <PersoCard event={event} key={event.uid} />
                ))}
                {nowAt === index && !(index === 0 && nowBeforeDeparture) && (
                  <NowMarker at={currentTime} />
                )}
                {!off && (
                  <TravelRow
                    item={item}
                    leg={leg}
                    hours={meteo.hours}
                    // The « Maison » card above already carries the departure time.
                    hideDeparture={item.id === firstStop?.id && homeCardShown}
                  />
                )}

                <li
                  className={classes.join(' ')}
                  style={{ '--hue': patientHue(item.patientId) } as CSSProperties}
                >
                  <span className="timeline__rail">
                    <span className="timeline__when">
                      <strong>{timeOf(item.startsAt)}</strong>
                      <small>{formatDuration(item.plannedDurationMin)}</small>
                      <TimeWeather at={timeOf(item.startsAt)} />
                    </span>
                    <span className="timeline__node" aria-hidden="true" />
                  </span>

                  <div className="timeline__item">
                    <Link
                      className="timeline__main"
                      to={`/rdv/${item.id}`}
                      state={{ from: returnTo }}
                    >
                      <span className="timeline__body">
                        <strong className="timeline__patient">
                          {titleOf(item)}
                          {/* Past, the compact pointage below already names the outcome. */}
                          {item.status !== 'planned' && !past && (
                            <span className="badge badge--muted">{STATUS_LABELS[item.status]}</span>
                          )}
                        </strong>
                        {item.locationKind === 'visio' ? (
                          <span className="muted">
                            <span aria-hidden="true">{VISIO_GLYPH}</span> {item.locationLabel}
                          </span>
                        ) : (
                          <>
                            <span className="muted">{item.locationLabel}</span>
                            <span className="muted timeline__address">{item.locationAddress}</span>
                            {maps.key !== null && item.lat === null && (
                              <span className="muted timeline__address">Adresse non localisée</span>
                            )}
                          </>
                        )}
                      </span>
                    </Link>

                    {clashes.map((clash) => (
                      <p className="note-sun" key={clash.event.uid}>
                        {conflictLabel(clash)}
                      </p>
                    ))}

                    {past && <OutcomeButtons appointment={item} onLogged={onLogged} />}
                  </div>
                </li>
              </Fragment>
            );
          })}

          {persoBeforeReturn.map((event) => (
            <PersoCard event={event} key={event.uid} />
          ))}

          {endMarkerPos === 'beforeRow' && <NowMarker at={currentTime} />}

          {lastStop && <HomeRow last={lastStop} leg={returnLeg} home={home} hours={meteo.hours} />}

          {persoDuringReturn.map((event) => (
            <PersoCard event={event} key={event.uid} />
          ))}

          {endMarkerPos === 'beforeCard' && <NowMarker at={currentTime} />}
          {lastStop && arrivalTime !== null && (
            <HomeCard home={home} time={arrivalTime} label="retour" />
          )}
          {endMarkerPos === 'after' && <NowMarker at={currentTime} />}

          {persoAfterReturn.map((event) => (
            <PersoCard event={event} key={event.uid} />
          ))}
        </ol>
        </MeteoHoursContext.Provider>
      )}

      <DayMap
        appointments={appointments.data ?? []}
        legs={travel.data?.legs ?? []}
        home={home}
        maps={maps}
      />

      <Link
        className="fab"
        to={`/rdv/nouveau?date=${date}`}
        state={{ from: returnTo }}
        aria-label="Nouvelle séance"
      >
        +
      </Link>
    </section>
  );
}

/** The séances she travels to: a cancelled or missed one is nowhere on the route. */
function isStop(item: AppointmentWithDisplay): boolean {
  return item.status !== 'cancelled' && item.status !== 'no_show';
}

/** The wall clock, ticking so the « maintenant » marker keeps up with the day. */
function useClock(): LocalDateTime {
  const [value, setValue] = useState(now);

  useEffect(() => {
    // Twice a minute: the displayed minute is then never more than 30 s stale.
    const timer = setInterval(() => setValue(now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  return value;
}

function NowMarker({ at }: { at: LocalDateTime }) {
  return (
    <li className="timeline__entry timeline__entry--now">
      <span className="timeline__rail">
        <span className="timeline__when">
          <strong>{timeOf(at)}</strong>
          <TimeWeather at={timeOf(at)} />
        </span>
        <span className="timeline__node" aria-hidden="true" />
      </span>
      <p className="now">maintenant</p>
    </li>
  );
}

/**
 * Un rendez-vous perso à sa place dans la journée. Gris, sans lien vers rien : il
 * n'appartient pas à l'app. Aucun trajet ne mène à lui ni n'en part — la carte et
 * les temps de vélo restent l'affaire des seules séances.
 */
function PersoCard({ event }: { event: PersoEvent }) {
  return (
    <li className="timeline__entry timeline__entry--perso">
      <span className="timeline__rail">
        <span className="timeline__when">
          <strong>{timeOf(event.startsAt)}</strong>
          <small>perso</small>
          <TimeWeather at={timeOf(event.startsAt)} />
        </span>
        <span className="timeline__node timeline__node--perso" aria-hidden="true" />
      </span>
      <div className="timeline__item timeline__item--perso">
        <span className="timeline__body">
          <strong className="timeline__patient">{event.title}</strong>
          {event.location !== null && (
            <a
              className="travel__link muted timeline__address"
              href={directionsUrl(event.location)}
              target="_blank"
              rel="noopener"
            >
              {event.location}
            </a>
          )}
        </span>
      </div>
    </li>
  );
}

function directionsUrl(destination: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=bicycling`;
}

/** When she is back home: last séance's end plus the ride, or the end itself if the day ends there. */
function arrivalHome(last: AppointmentWithDisplay, leg: TravelLeg | null): string {
  const end = endsAt(last.startsAt, last.plannedDurationMin);
  const lead = departureLeadMin(leg?.bikeMinutes ?? null);
  return timeOf(lead === null ? end : addMinutes(end, lead));
}

/**
 * « Maison » bookends the day: the departure time on the way out, the estimated
 * return on the way back. Hidden while « Maison » is not set.
 */
function HomeCard({
  home,
  time,
  label,
}: {
  home: HomeSettings | null;
  time: string;
  label: string;
}) {
  const address = home?.formattedAddress ?? home?.address ?? '';
  if (!address) return null;

  return (
    <li className="timeline__entry timeline__entry--home">
      <span className="timeline__rail">
        <span className="timeline__when">
          <strong>{time}</strong>
          <small>{label}</small>
          <TimeWeather at={time} />
        </span>
        <span className="timeline__node timeline__node--home" aria-hidden="true" />
      </span>
      <div className="timeline__item timeline__item--home">
        <span className="timeline__body">
          <strong className="timeline__patient">
            <span aria-hidden="true">🏠</span> Maison
          </strong>
          <span className="muted timeline__address">{address}</span>
        </span>
      </div>
    </li>
  );
}

/**
 * The hop into a séance: a tap-to-navigate link, plus the bike estimate when the
 * server could compute one. Without a Google key there is no leg at all, and the
 * link alone is still worth showing — Google Maps takes a raw address. A visio
 * takes her home, and a leg of zero minutes means she is already there.
 */
function TravelRow({
  item,
  leg,
  hours,
  hideDeparture = false,
}: {
  item: AppointmentWithDisplay;
  leg: TravelLeg | null;
  hours: MeteoHour[];
  hideDeparture?: boolean;
}) {
  const visio = item.locationKind === 'visio';
  const destination = item.locationAddress.trim() || (visio ? '' : item.locationLabel.trim());
  if (!destination || leg?.bikeMinutes === 0) return null;

  const { estimate, departure, tight, gapMin } = hopOf(leg, item.startsAt);

  return (
    <li className="timeline__entry timeline__entry--hop">
      <span className="timeline__rail">
        {departure !== null && !hideDeparture && (
          <>
            <span className="timeline__when timeline__when--hop">
              <strong>{departure}</strong>
              <TimeWeather at={departure} />
            </span>
            <span className="timeline__node timeline__node--hop" aria-hidden="true" />
          </>
        )}
      </span>
      <p className={`travel${tight ? ' travel--tight' : ''}`}>
        <span aria-hidden="true">🚲</span>{' '}
        <a className="travel__link" href={directionsUrl(destination)} target="_blank" rel="noopener">
          Aller à {visio ? 'la maison' : destination}
        </a>
        {estimate !== null && (
          <>
            {' — '}Estimé : {estimate} min à vélo{leg?.fromHome && ' depuis la maison'}
          </>
        )}
        {tight && ` — l’écart n’est que de ${gapMin} min`}
        <RideWeather hours={hours} at={departure} />
      </p>
    </li>
  );
}

/**
 * The ride closing the day. She leaves when the last séance ends, and nothing
 * waits for her at the other end: no tight warning here. Absent when the day
 * already ends at home — a visio — or when « Maison » has no address.
 */
function HomeRow({
  last,
  leg,
  home,
  hours,
}: {
  last: AppointmentWithDisplay;
  leg: TravelLeg | null;
  home: HomeSettings | null;
  hours: MeteoHour[];
}) {
  const destination = home?.formattedAddress ?? home?.address ?? '';
  if (!destination || last.locationKind === 'visio' || leg?.bikeMinutes === 0) return null;

  const bikeMinutes = leg?.bikeMinutes ?? null;
  const leavesAt = timeOf(endsAt(last.startsAt, last.plannedDurationMin));

  return (
    <li className="timeline__entry timeline__entry--hop">
      <span className="timeline__rail">
        <span className="timeline__when timeline__when--hop">
          <strong>{leavesAt}</strong>
          <TimeWeather at={leavesAt} />
        </span>
        <span className="timeline__node timeline__node--hop" aria-hidden="true" />
      </span>
      <p className="travel">
        <span aria-hidden="true">🚲</span>{' '}
        <a className="travel__link" href={directionsUrl(destination)} target="_blank" rel="noopener">
          Rentrer à la maison
        </a>
        {bikeMinutes !== null && (
          <>
            {' — '}Estimé : {departureLeadMin(bikeMinutes)} min à vélo
          </>
        )}
        <RideWeather hours={hours} at={leavesAt} />
      </p>
    </li>
  );
}

/**
 * L'emoji du temps qu'il fera au moment de partir — et rien du tout par temps
 * clément : une journée sans pluie laisse la timeline tranquille.
 */
function RideWeather({ hours, at }: { hours: MeteoHour[]; at: string | null }) {
  const hour = at === null ? null : hourAt(hours, at);
  if (hour === null) return null;
  const emoji = rideEmoji(hour);
  if (emoji === null) return null;

  return (
    <>
      {' '}
      <span className="travel__meteo" title={formatDegrees(hour.tempC)}>
        {emoji}
      </span>
    </>
  );
}

/**
 * The day's route, from « Maison » when it is located. Hidden entirely without a
 * browser key or without a single located séance, and replaced by the old
 * placeholder when the key is missing — she should know why the map is not there.
 */
function DayMap({
  appointments,
  legs,
  home,
  maps: { key, resolved },
}: {
  appointments: AppointmentWithDisplay[];
  legs: TravelLeg[];
  home: HomeSettings | null;
  maps: ReturnType<typeof useMapsKey>;
}) {
  // The route is the one she actually rides: a cancelled séance is not a stop,
  // exactly as the server's legs have it, and each stop carries the ride into it.
  const points = useMemo<MapPoint[]>(() => {
    const stops = appointments
      .filter(isStop)
      .flatMap<MapPoint>((item, index) =>
        item.lat === null || item.lng === null
          ? []
          : [
              {
                lat: item.lat,
                lng: item.lng,
                label: String(index + 1),
                title: `${timeOf(item.startsAt)} · ${item.patientName} — ${item.locationLabel}`,
                encodedPolyline: legInto(legs, item.id)?.encodedPolyline ?? null,
              },
            ],
      );

    if (stops.length === 0 || home?.lat == null || home.lng == null) return stops;
    const house: MapPoint = {
      lat: home.lat,
      lng: home.lng,
      home: true,
      title: `Maison — ${home.formattedAddress ?? home.address}`,
    };

    // The ride back closes the loop, unless the day already ends at home.
    const back = legHome(legs);
    return [
      house,
      ...stops,
      ...(back ? [{ ...house, encodedPolyline: back.encodedPolyline }] : []),
    ];
  }, [appointments, legs, home]);

  if (!resolved) return null;

  if (!key) {
    return (
      <div className="map-slot">
        <span>Carte du trajet</span>
        <small>Clé Google Maps absente.</small>
      </div>
    );
  }

  if (points.length === 0) return null;
  return <MapCanvas points={points} route title="Trajet de la journée" />;
}
